import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { religions, castes, stars, raasis, motherTongues, countries, indianStates, occupationCategories, familyTypes, familyStatuses, familyValues, annualIncomeRanges } from '../../data/profileOptions';
import './Admin.css';

const AdminUserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [basic, setBasic] = useState({});
  const [religious, setReligious] = useState({});
  const [professional, setProfessional] = useState({});
  const [location, setLocation] = useState({});
  const [family, setFamily] = useState({});
  const [lifestyle, setLifestyle] = useState({});

  useEffect(() => {
    adminService.getUser(id)
      .then(res => {
        const u = res.data;
        setUser(u);
        const p = u.profile || {};
        setBasic({
          first_name: p.first_name || '', last_name: p.last_name || '',
          height: p.height || '', weight: p.weight || '',
          marital_status: p.marital_status || '', physical_status: p.physical_status || '',
          mother_tongue: p.mother_tongue || '', profile_created_by: p.profile_created_by || '',
          about_me: p.about_me || '',
        });
        const r = p.religious_details || {};
        setReligious({
          religion: r.religion || '', caste: r.caste || '', sub_caste: r.sub_caste || '',
          gotra: r.gotra || '', star: r.star || '', raasi: r.raasi || '',
        });
        const pr = p.professional_details || {};
        setProfessional({
          occupation: pr.occupation || '', occupation_category: pr.occupation_category || '',
          company_name: pr.company_name || '', annual_income: pr.annual_income || '',
          currency: pr.currency || '', work_location: pr.work_location || '',
        });
        const l = p.location || {};
        setLocation({
          country: l.country || '', state: l.state || '', city: l.city || '',
          zip_code: l.zip_code || '', citizenship: l.citizenship || '', residing_since: l.residing_since || '',
        });
        const f = p.family_details || {};
        setFamily({
          father_name: f.father_name || '', father_occupation: f.father_occupation || '',
          mother_name: f.mother_name || '', mother_occupation: f.mother_occupation || '',
          siblings: f.siblings || '', family_type: f.family_type || '',
          family_status: f.family_status || '', family_values: f.family_values || '',
        });
        const ls = p.lifestyle || {};
        setLifestyle({
          diet: ls.diet || '', smoking: ls.smoking || '', drinking: ls.drinking || '',
          hobbies: ls.hobbies || '', interests: ls.interests || '',
        });
        setPhotos(p.photos || []);
      })
      .catch(() => navigate('/admin/users'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await adminService.updateUserProfile(id, {
        ...basic, religious, professional, location, family, lifestyle,
      });
      setMsg({ type: 'success', text: 'Profile updated successfully.' });
      window.scrollTo(0, 0);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Update failed.' });
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await adminService.uploadPhoto(id, formData);
      setPhotos([...photos, res.data.photo]);
      setMsg({ type: 'success', text: 'Photo uploaded.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Upload failed.' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await adminService.deletePhoto(id, photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
      setMsg({ type: 'success', text: 'Photo deleted.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Delete failed.' });
    }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      await adminService.setPrimaryPhoto(id, photoId);
      setPhotos(photos.map(p => ({ ...p, is_primary: p.id === photoId })));
      setMsg({ type: 'success', text: 'Primary photo updated.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed.' });
    }
  };

  const handleApprovePhoto = async (photoId) => {
    try {
      await adminService.approvePhoto(id, photoId);
      setPhotos(photos.map(p => p.id === photoId ? { ...p, is_approved: true } : p));
      setMsg({ type: 'success', text: 'Photo approved.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed.' });
    }
  };

  const handleRejectPhoto = async (photoId) => {
    if (!window.confirm('Reject and delete this photo?')) return;
    try {
      await adminService.rejectPhoto(id, photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
      setMsg({ type: 'success', text: 'Photo rejected and deleted.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed.' });
    }
  };

  const field = (label, stateObj, setStateObj, name, type = 'text') => (
    <div className="form-group">
      <label>{label}</label>
      <input type={type} value={stateObj[name] || ''} onChange={e => setStateObj({ ...stateObj, [name]: e.target.value })} />
    </div>
  );

  const selectField = (label, stateObj, setStateObj, name, options) => (
    <div className="form-group">
      <label>{label}</label>
      <select value={stateObj[name] || ''} onChange={e => setStateObj({ ...stateObj, [name]: e.target.value })}>
        <option value="">Select</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  if (loading) return <div>Loading...</div>;

  return (
    <div className="admin-page">
      <Link to="/admin/users" className="admin-back-btn">← Back to Users</Link>
      <h1>Edit User: {user?.email}</h1>
      {msg && <div className={`admin-msg ${msg.type}`}>{msg.text}</div>}

      <form className="admin-form" onSubmit={handleSubmit}>
        <h3 style={{ marginBottom: 16 }}>Basic Information</h3>
        <div className="form-row">
          {field('First Name', basic, setBasic, 'first_name')}
          {field('Last Name', basic, setBasic, 'last_name')}
        </div>
        <div className="form-row">
          {field('Height (cm)', basic, setBasic, 'height', 'number')}
          {field('Weight (kg)', basic, setBasic, 'weight', 'number')}
        </div>
        <div className="form-row">
          {selectField('Marital Status', basic, setBasic, 'marital_status', [
            { value: 'never_married', label: 'Never Married' }, { value: 'divorced', label: 'Divorced' },
            { value: 'widowed', label: 'Widowed' }, { value: 'separated', label: 'Separated' },
          ])}
          {field('Physical Status', basic, setBasic, 'physical_status')}
        </div>
        <div className="form-row">
          {selectField('Mother Tongue', basic, setBasic, 'mother_tongue', motherTongues.map(m => ({ value: m, label: m })))}
          {field('Profile Created By', basic, setBasic, 'profile_created_by')}
        </div>
        <div className="form-group">
          <label>About Me</label>
          <textarea rows={3} value={basic.about_me || ''} onChange={e => setBasic({ ...basic, about_me: e.target.value })} />
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Religious Details</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Religion</label>
            <select value={religious.religion || ''} onChange={e => setReligious({ ...religious, religion: e.target.value, caste: '' })}>
              <option value="">Select</option>
              {religions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {selectField('Caste', religious, setReligious, 'caste', (castes[religious.religion] || []).map(c => ({ value: c, label: c })))}
        </div>
        <div className="form-row">
          {field('Sub Caste', religious, setReligious, 'sub_caste')}
          {field('Gotra', religious, setReligious, 'gotra')}
        </div>
        <div className="form-row">
          {selectField('Star', religious, setReligious, 'star', stars.map(s => ({ value: s, label: s })))}
          {selectField('Raasi', religious, setReligious, 'raasi', raasis.map(r => ({ value: r, label: r })))}
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Professional Details</h3>
        <div className="form-row">
          {field('Occupation', professional, setProfessional, 'occupation')}
          {selectField('Occupation Category', professional, setProfessional, 'occupation_category', occupationCategories)}
        </div>
        <div className="form-row">
          {field('Company Name', professional, setProfessional, 'company_name')}
          {field('Work Location', professional, setProfessional, 'work_location')}
        </div>
        <div className="form-row">
          {selectField('Annual Income', professional, setProfessional, 'annual_income', annualIncomeRanges)}
          {field('Currency', professional, setProfessional, 'currency')}
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Location</h3>
        <div className="form-row">
          {selectField('Country', location, setLocation, 'country', countries.map(c => ({ value: c, label: c })))}
          {selectField('State', location, setLocation, 'state', indianStates.map(s => ({ value: s, label: s })))}
        </div>
        <div className="form-row">
          {field('City', location, setLocation, 'city')}
          {field('Zip Code', location, setLocation, 'zip_code')}
        </div>
        <div className="form-row">
          {field('Citizenship', location, setLocation, 'citizenship')}
          {field('Residing Since', location, setLocation, 'residing_since')}
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Family Details</h3>
        <div className="form-row">
          {field("Father's Name", family, setFamily, 'father_name')}
          {field("Father's Occupation", family, setFamily, 'father_occupation')}
        </div>
        <div className="form-row">
          {field("Mother's Name", family, setFamily, 'mother_name')}
          {field("Mother's Occupation", family, setFamily, 'mother_occupation')}
        </div>
        <div className="form-row">
          {field('Siblings', family, setFamily, 'siblings')}
          {selectField('Family Type', family, setFamily, 'family_type', familyTypes)}
        </div>
        <div className="form-row">
          {selectField('Family Status', family, setFamily, 'family_status', familyStatuses)}
          {selectField('Family Values', family, setFamily, 'family_values', familyValues)}
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Lifestyle</h3>
        <div className="form-row">
          {selectField('Diet', lifestyle, setLifestyle, 'diet', [
            { value: 'vegetarian', label: 'Vegetarian' }, { value: 'non_vegetarian', label: 'Non-Vegetarian' },
            { value: 'eggetarian', label: 'Eggetarian' }, { value: 'vegan', label: 'Vegan' },
          ])}
          {selectField('Smoking', lifestyle, setLifestyle, 'smoking', [
            { value: 'no', label: 'No' }, { value: 'occasionally', label: 'Occasionally' }, { value: 'yes', label: 'Yes' },
          ])}
        </div>
        <div className="form-row">
          {selectField('Drinking', lifestyle, setLifestyle, 'drinking', [
            { value: 'no', label: 'No' }, { value: 'occasionally', label: 'Occasionally' }, { value: 'yes', label: 'Yes' },
          ])}
          {field('Hobbies', lifestyle, setLifestyle, 'hobbies')}
        </div>
        <div className="form-group">
          <label>Interests</label>
          <input value={lifestyle.interests || ''} onChange={e => setLifestyle({ ...lifestyle, interests: e.target.value })} />
        </div>

        <h3 style={{ margin: '24px 0 16px' }}>Photos</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {photos.map(photo => (
            <div key={photo.id} style={{ position: 'relative', border: photo.is_primary ? '3px solid #c0392b' : '1px solid #ddd', borderRadius: 8, overflow: 'hidden', width: 140 }}>
              <img src={`http://localhost:8000${photo.photo_url}`} alt="User" style={{ width: 140, height: 140, objectFit: 'cover', display: 'block', opacity: photo.is_approved ? 1 : 0.5 }} />
              {photo.is_primary && <div style={{ position: 'absolute', top: 4, left: 4, background: '#c0392b', color: 'white', fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4 }}>Primary</div>}
              <div style={{ position: 'absolute', top: 4, right: 4, background: photo.is_approved ? '#27ae60' : '#e67e22', color: 'white', fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4 }}>
                {photo.is_approved ? 'Approved' : 'Pending'}
              </div>
              <div style={{ display: 'flex', gap: 2, padding: 4, flexWrap: 'wrap' }}>
                {!photo.is_approved && (
                  <button type="button" onClick={() => handleApprovePhoto(photo.id)} style={{ flex: 1, fontSize: '0.7rem', padding: '3px', border: 'none', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer', borderRadius: 3 }}>Approve</button>
                )}
                {!photo.is_approved && (
                  <button type="button" onClick={() => handleRejectPhoto(photo.id)} style={{ flex: 1, fontSize: '0.7rem', padding: '3px', border: 'none', background: '#fff3e0', color: '#e65100', cursor: 'pointer', borderRadius: 3 }}>Reject</button>
                )}
                {!photo.is_primary && (
                  <button type="button" onClick={() => handleSetPrimary(photo.id)} style={{ flex: 1, fontSize: '0.7rem', padding: '3px', border: 'none', background: '#e3f2fd', color: '#1565c0', cursor: 'pointer', borderRadius: 3 }}>Primary</button>
                )}
                <button type="button" onClick={() => handleDeletePhoto(photo.id)} style={{ flex: 1, fontSize: '0.7rem', padding: '3px', border: 'none', background: '#fce4ec', color: '#c62828', cursor: 'pointer', borderRadius: 3 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
        <div className="form-group">
          <label>Upload New Photo</label>
          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleUploadPhoto} disabled={uploading} />
          {uploading && <p style={{ fontSize: '0.8rem', color: '#888' }}>Uploading...</p>}
        </div>

        <button type="submit" className="btn-primary" disabled={saving} style={{ marginTop: 24 }}>
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </form>
    </div>
  );
};

export default AdminUserEdit;
